import { useMutation } from "@apollo/client";
import { useNavigate } from "react-router-dom";

import FlashContext from "components/FlashMessages/FlashContext";
import PageLoading from "components/loading/PageLoading";
import useTypedContext from "hooks/useTypedContext";
import { Session } from "types/generated";
import { AccountContext } from "views/AccountWrapper";
import Timestamp from "components/Timestamp";
import Typography from "ds/components/Typography";
import Box from "ds/components/Box";

import { SESSION_DELETE } from "../gql";
import { showSessionsConfirmationModal } from "../ConfirmationModal";
import styles from "./styles.module.css";

type ItemProps = {
  session: Session;
};

const Item = ({ session }: ItemProps) => {
  const navigate = useNavigate();

  const { viewer } = useTypedContext(AccountContext);
  const { reportSuccess, onError } = useTypedContext(FlashContext);

  const [sessionDelete, { loading }] = useMutation(SESSION_DELETE, {
    variables: { id: session.id },
    refetchQueries: ["GetSessions"],
  });

  const handleDeactivation = () => {
    const asyncFn = async () => {
      if (session.current) {
        return navigate("/logout");
      }

      try {
        await sessionDelete();
        reportSuccess({ message: `Session ${session.id} was deactivated` });
      } catch (error) {
        onError(error);
      }
    };

    void asyncFn();
  };

  const handleDeactivationConfirmation = () =>
    showSessionsConfirmationModal({
      confirmationCallback: handleDeactivation,
      sessionId: session.id,
      userId: session.login,
      isCurrentSession: session.current,
    });

  return (
    <Box className={styles.wrapper}>
      {loading && <PageLoading />}

      <Box className={styles.content} direction="column" padding="x-large">
        <Box gap="medium" margin="0 0 medium">
          <Typography variant="p-t8" tag="span">
            {session.login}
          </Typography>
          <Typography variant="s-body3" tag="span" color="secondary">
            {session.id}
          </Typography>
        </Box>

        <ul className={styles.panel}>
          <li>
            Created on <Timestamp variant="p-body4" timestamp={session.createdAt} />
          </li>
          <li>
            Expires on <Timestamp variant="p-body4" timestamp={session.expiresAt} />
          </li>
          <li>
            <Typography variant="p-body4" tag="span">
              {session.ipAddress}
            </Typography>
          </li>
          <li>
            <Typography variant="p-body4" tag="span">
              {session.type}
            </Typography>
          </li>
        </ul>
      </Box>

      {viewer.admin && (
        <button className={styles.action} onClick={handleDeactivationConfirmation} type="button">
          {session.current ? "Logout" : "Deactivate"}
        </button>
      )}
    </Box>
  );
};

export default Item;
